import { useState, useEffect } from 'react';
import {
  ShoppingCart,
  CalendarDays,
  Archive,
  Users,
  Truck,
  AlertTriangle,
  TrendingUp,
  Trophy,
  AlertCircle,
  Package,
  Banknote,
  Receipt,
  UserCheck,
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
  CreditCard,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';

function formatNumber(n) {
  const num = Number(n);
  return isNaN(num) ? '0' : num.toLocaleString();
}

function formatCurrency(n) {
  const num = Number(n);
  return isNaN(num) ? '0' : num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export default function Dashboard() {
  const [data, setData] = useState(() => {
    try {
      const cached = localStorage.getItem('gaza_dashboard_cache');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => !data);

  const fetchDashboard = () => {
    api.get('/dashboard')
      .then((res) => {
        setData(res);
        try {
          localStorage.setItem('gaza_dashboard_cache', JSON.stringify(res));
        } catch {}
      })
      .catch((err) => toast.error(err.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const todaySales = {
    orders: Number(data?.todaySales?.orders ?? 0),
    total: Number(data?.todaySales?.total ?? 0),
    cash: Number(data?.todaySales?.cash ?? 0),
    card: Number(data?.todaySales?.card ?? 0),
    online: Number(data?.todaySales?.online ?? 0),
    credit: Number(data?.todaySales?.credit ?? 0),
  };
  const todayPurchases = {
    todayOrders: Number(data?.todayPurchases?.todayOrders ?? 0),
    todayTotal: Number(data?.todayPurchases?.todayTotal ?? 0),
    todayPaid: Number(data?.todayPurchases?.todayPaid ?? 0),
    monthOrders: Number(data?.todayPurchases?.monthOrders ?? 0),
    monthTotal: Number(data?.todayPurchases?.monthTotal ?? 0),
    monthPaid: Number(data?.todayPurchases?.monthPaid ?? 0),
  };
  const monthSales = data?.monthSales || { orders: 0, total: 0 };
  const stockValue = data?.stockValue || { value: 0, items: 0 };
  const payable = {
    total: data?.payable?.total ?? 0,
    fromPurchases: data?.payable?.fromPurchases ?? (data?.payable?.from_purchases ?? 0),
    fromBalances: data?.payable?.fromBalances ?? (data?.payable?.from_balances ?? 0),
  };
  const alerts = {
    lowStock: Number(data?.alerts?.lowStock ?? data?.alerts?.low_stock ?? 0),
    expiry: Number(data?.alerts?.expiry ?? 0),
  };
  const remainingCash = {
    totalBalance: Number(data?.remainingCash?.totalBalance ?? 0),
    todayCashIn: Number(data?.remainingCash?.todayCashIn ?? 0),
    todayCashOut: Number(data?.remainingCash?.todayCashOut ?? 0),
    todayNet: Number(data?.remainingCash?.todayNet ?? 0),
    details: data?.remainingCash?.details || {},
  };
  const digitalBalance = {
    totalBalance: Number(data?.digitalBalance?.totalBalance ?? 0),
    todayIn: Number(data?.digitalBalance?.todayIn ?? 0),
    todayOut: Number(data?.digitalBalance?.todayOut ?? 0),
  };
  const expenses = {
    todayTotal: Number(data?.expenses?.todayTotal ?? 0),
    todayCount: Number(data?.expenses?.todayCount ?? 0),
    monthTotal: Number(data?.expenses?.monthTotal ?? 0),
    monthCount: Number(data?.expenses?.monthCount ?? 0),
  };
  const staffPaid = {
    todayTotal: Number(data?.staffPaid?.todayTotal ?? 0),
    todaySalary: Number(data?.staffPaid?.todaySalary ?? 0),
    todayAdvance: Number(data?.staffPaid?.todayAdvance ?? 0),
    todayCount: Number(data?.staffPaid?.todayCount ?? 0),
    monthTotal: Number(data?.staffPaid?.monthTotal ?? 0),
    monthSalary: Number(data?.staffPaid?.monthSalary ?? 0),
    monthAdvance: Number(data?.staffPaid?.monthAdvance ?? 0),
    monthCount: Number(data?.staffPaid?.monthCount ?? 0),
  };
  const udhaarReceived = {
    todayTotal: Number(data?.udhaarReceived?.todayTotal ?? 0),
    todayCount: Number(data?.udhaarReceived?.todayCount ?? 0),
    monthTotal: Number(data?.udhaarReceived?.monthTotal ?? 0),
    monthCount: Number(data?.udhaarReceived?.monthCount ?? 0),
    allTimeTotal: Number(data?.udhaarReceived?.allTimeTotal ?? 0),
    allTimeCount: Number(data?.udhaarReceived?.allTimeCount ?? 0),
  };
  const udhaarGiven = {
    todayTotal: Number(data?.udhaarGiven?.todayTotal ?? 0),
    todayCount: Number(data?.udhaarGiven?.todayCount ?? 0),
    monthTotal: Number(data?.udhaarGiven?.monthTotal ?? 0),
    monthCount: Number(data?.udhaarGiven?.monthCount ?? 0),
  };
  const advances = {
    customerReceived: Number(data?.advances?.customerReceived ?? 0),
    customerCount: Number(data?.advances?.customerCount ?? 0),
    supplierPaid: Number(data?.advances?.supplierPaid ?? 0),
    supplierCount: Number(data?.advances?.supplierCount ?? 0),
  };
  const advanceNet = advances.supplierPaid - advances.customerReceived;

  const cashTransactions = {
    todayDeposits: Number(data?.cashTransactions?.todayDeposits ?? 0),
    todayWithdrawals: Number(data?.cashTransactions?.todayWithdrawals ?? 0),
    monthDeposits: Number(data?.cashTransactions?.monthDeposits ?? 0),
    monthWithdrawals: Number(data?.cashTransactions?.monthWithdrawals ?? 0),
    allTimeDeposits: Number(data?.cashTransactions?.allTimeDeposits ?? 0),
    allTimeWithdrawals: Number(data?.cashTransactions?.allTimeWithdrawals ?? 0),
  };

  const dailyProfit = {
    netProfit: Number(data?.dailyProfit?.netProfit ?? 0),
    grossProfit: Number(data?.dailyProfit?.grossProfit ?? 0),
    cogs: Number(data?.dailyProfit?.cogs ?? 0),
    sales: Number(data?.dailyProfit?.sales ?? 0),
    operatingExpenses: Number(data?.dailyProfit?.operatingExpenses ?? 0),
    margin: Number(data?.dailyProfit?.margin ?? 0),
    grossMargin: Number(data?.dailyProfit?.grossMargin ?? 0),
  };

  const trend      = data?.revenueTrend || [];
  const topSellers = data?.topSellers || [];
  const expiryAlerts = data?.expiryAlerts || [];
  const lowStock     = data?.lowStock || [];

  const monthLabel = new Date().toLocaleString('default', { month: 'short', year: 'numeric' });

  const metrics = [
    {
      icon: Banknote,
      label: 'REMAINING CASH',
      value: formatCurrency(remainingCash.totalBalance),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-800 border border-emerald-200/60" title="Today Cash In">
            <span className="text-emerald-500 font-semibold">In:</span> {formatCurrency(remainingCash.todayCashIn)}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 font-medium text-rose-800 border border-rose-200/60" title="Today Cash Out">
            <span className="text-rose-400 font-semibold">Out:</span> {formatCurrency(remainingCash.todayCashOut)}
          </span>
        </div>
      ),
      iconBg: 'bg-emerald-100 text-emerald-600',
      highlight: true,
    },
    {
      icon: CreditCard,
      label: 'CARD / DIGITAL BALANCE',
      value: formatCurrency(digitalBalance.totalBalance),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 font-medium text-blue-800 border border-blue-200/60">
            <span className="text-blue-600 font-semibold">In:</span> {formatCurrency(digitalBalance.todayIn)}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 font-medium text-rose-800 border border-rose-200/60">
            <span className="text-rose-500 font-semibold">Out:</span> {formatCurrency(digitalBalance.todayOut)}
          </span>
        </div>
      ),
      iconBg: 'bg-blue-100 text-blue-600',
    },
    {
      icon: ShoppingCart,
      label: "TODAY'S SALES",
      value: formatCurrency(todaySales.total),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-800 border border-emerald-200/60" title="Cash Sales">
            <span className="text-emerald-600 font-bold">Cash:</span> {formatCurrency(todaySales.cash)}
          </span>
          <span className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 font-medium text-blue-800 border border-blue-200/60" title="Card / Bank Sales">
            <span className="text-blue-600 font-bold">Card:</span> {formatCurrency(Number(todaySales.card || 0) + Number(todaySales.online || 0))}
          </span>
          <span className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-800 border border-amber-200/60" title="Udhaar (Credit) Sales">
            <span className="text-amber-600 font-bold">Udhaar:</span> {formatCurrency(todaySales.credit)}
          </span>
        </div>
      ),
      iconBg: 'bg-blue-100 text-blue-600',
    },
    {
      icon: TrendingUp,
      label: 'DAILY PROFIT',
      value: formatCurrency(dailyProfit.netProfit),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className={clsx(
            "inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 font-medium border",
            dailyProfit.netProfit >= 0
              ? "bg-emerald-50 text-emerald-800 border-emerald-200/60"
              : "bg-rose-50 text-rose-800 border-rose-200/60"
          )} title={`Gross Profit: Rs. ${formatCurrency(dailyProfit.grossProfit)} | COGS: Rs. ${formatCurrency(dailyProfit.cogs)} | Expenses: Rs. ${formatCurrency(dailyProfit.operatingExpenses)}`}>
            <span className={dailyProfit.netProfit >= 0 ? "text-emerald-600 font-bold" : "text-rose-600 font-bold"}>
              {dailyProfit.margin}%
            </span> margin
          </span>
          <span className="inline-flex items-center gap-0.5 rounded bg-slate-50 px-1.5 py-0.5 font-medium text-slate-700 border border-slate-200/60" title="Gross Profit">
            <span className="text-slate-500 font-semibold">Gross:</span> {formatCurrency(dailyProfit.grossProfit)}
          </span>
        </div>
      ),
      iconBg: dailyProfit.netProfit >= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600',
      highlight: true,
    },
    {
      icon: Truck,
      label: "TODAY'S PURCHASES",
      value: formatCurrency(todayPurchases.todayTotal),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-purple-50 px-1.5 py-0.5 font-medium text-purple-800 border border-purple-200/60" title="Today Orders">
            <span className="h-1.5 w-1.5 rounded-full bg-purple-500"></span>
            {formatNumber(todayPurchases.todayOrders)} orders
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-purple-50 px-1.5 py-0.5 font-medium text-purple-800 border border-purple-200/60" title="Month Purchases">
            <span className="text-purple-600 font-semibold">Month:</span> {formatCurrency(todayPurchases.monthTotal)}
          </span>
        </div>
      ),
      iconBg: 'bg-purple-100 text-purple-600',
    },
    {
      icon: CalendarDays,
      label: 'MONTH SALES',
      value: formatCurrency(monthSales.total),
      badge: (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500"></span>
          {monthLabel} ({formatNumber(monthSales.orders)} orders)
        </span>
      ),
      iconBg: 'bg-blue-100 text-blue-500',
    },
    {
      icon: Receipt,
      label: 'EXPENSES',
      value: formatCurrency(expenses.todayTotal),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 font-medium text-rose-800 border border-rose-200/60" title="Month Expenses">
            <span className="text-rose-500 font-semibold">Month:</span> {formatCurrency(expenses.monthTotal)}
          </span>
        </div>
      ),
      iconBg: 'bg-rose-100 text-rose-600',
    },
    {
      icon: UserCheck,
      label: 'STAFF PAID',
      value: formatCurrency(staffPaid.todayTotal),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-0.5 rounded bg-indigo-50 px-1.5 py-0.5 font-medium text-indigo-800 border border-indigo-200/60" title={`Salary Today: Rs. ${formatCurrency(staffPaid.todaySalary)} | Month: Rs. ${formatCurrency(staffPaid.monthSalary)}`}>
            <span className="text-indigo-600 font-bold">Sal:</span> {formatCurrency(staffPaid.todaySalary)}
          </span>
          <span className="inline-flex items-center gap-0.5 rounded bg-purple-50 px-1.5 py-0.5 font-medium text-purple-800 border border-purple-200/60" title={`Advance Today: Rs. ${formatCurrency(staffPaid.todayAdvance)} | Month: Rs. ${formatCurrency(staffPaid.monthAdvance)}`}>
            <span className="text-purple-600 font-bold">Adv:</span> {formatCurrency(staffPaid.todayAdvance)}
          </span>
        </div>
      ),
      iconBg: 'bg-indigo-100 text-indigo-600',
    },
    {
      icon: ArrowDownLeft,
      label: 'UDHAAR RECEIVED',
      value: formatCurrency(udhaarReceived.todayTotal),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-teal-50 px-1.5 py-0.5 font-medium text-teal-800 border border-teal-200/60" title="Month Udhaar Received">
            <span className="text-teal-600 font-semibold">Month:</span> {formatCurrency(udhaarReceived.monthTotal)}
          </span>
        </div>
      ),
      iconBg: 'bg-teal-100 text-teal-600',
    },
    {
      icon: ArrowUpRight,
      label: 'UDHAAR GIVEN',
      value: formatCurrency(udhaarGiven.todayTotal),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-800 border border-amber-200/60" title="Month Udhaar Given">
            <span className="text-amber-600 font-semibold">Month:</span> {formatCurrency(udhaarGiven.monthTotal)}
          </span>
        </div>
      ),
      iconBg: 'bg-amber-100 text-amber-600',
    },
    {
      icon: ArrowDownLeft,
      label: 'CASH DEPOSITS',
      value: formatCurrency(cashTransactions.todayDeposits),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-800 border border-emerald-200/60" title="Month Deposits">
            <span className="text-emerald-600 font-semibold">Month:</span> {formatCurrency(cashTransactions.monthDeposits)}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-slate-50 px-1.5 py-0.5 font-medium text-slate-700 border border-slate-200/60" title="All-Time Deposits">
            <span className="text-slate-500 font-semibold">Total:</span> {formatCurrency(cashTransactions.allTimeDeposits)}
          </span>
        </div>
      ),
      iconBg: 'bg-emerald-100 text-emerald-600',
    },
    {
      icon: ArrowUpRight,
      label: 'CASH WITHDRAWALS',
      value: formatCurrency(cashTransactions.todayWithdrawals),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 font-medium text-rose-800 border border-rose-200/60" title="Month Withdrawals">
            <span className="text-rose-500 font-semibold">Month:</span> {formatCurrency(cashTransactions.monthWithdrawals)}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-slate-50 px-1.5 py-0.5 font-medium text-slate-700 border border-slate-200/60" title="All-Time Withdrawals">
            <span className="text-slate-500 font-semibold">Total:</span> {formatCurrency(cashTransactions.allTimeWithdrawals)}
          </span>
        </div>
      ),
      iconBg: 'bg-rose-100 text-rose-600',
    },
    {
      icon: Archive,
      label: 'STOCK VALUE',
      value: formatCurrency(stockValue.value),
      badge: (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-purple-50 px-2 py-0.5 text-[11px] font-semibold text-purple-700">
          <span className="h-1.5 w-1.5 rounded-full bg-purple-500"></span>
          {formatNumber(stockValue.items)} items
        </span>
      ),
      iconBg: 'bg-purple-100 text-purple-600',
    },
    {
      icon: Wallet,
      label: 'ADVANCE PAYMENTS',
      value: formatCurrency(advanceNet),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-teal-50 px-1.5 py-0.5 font-medium text-teal-800 border border-teal-200/60" title="Advance received from customers">
            <span className="text-teal-600 font-semibold">Cust:</span> -{formatCurrency(advances.customerReceived)}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-purple-50 px-1.5 py-0.5 font-medium text-purple-800 border border-purple-200/60" title="Advance paid to suppliers">
            <span className="text-purple-600 font-semibold">Supp:</span> +{formatCurrency(advances.supplierPaid)}
          </span>
        </div>
      ),
      iconBg: 'bg-violet-100 text-violet-600',
    },
    {
      icon: Truck,
      label: 'PAYABLE',
      value: formatCurrency(payable.total),
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 font-medium text-rose-800 border border-rose-200/60" title="Unpaid Purchases">
            <span className="text-rose-400 font-semibold">Purch:</span> {formatCurrency(payable.fromPurchases)}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 font-medium text-rose-800 border border-rose-200/60" title="Supplier Balances">
            <span className="text-rose-400 font-semibold">Bal:</span> {formatCurrency(payable.fromBalances)}
          </span>
        </div>
      ),
      iconBg: 'bg-red-100 text-red-500',
    },
    {
      icon: AlertTriangle,
      label: 'ALERTS',
      value: alerts.lowStock + alerts.expiry,
      badge: (
        <div className="flex flex-wrap items-center gap-1 text-[10px]">
          <span className={clsx(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium border',
            alerts.lowStock > 0 ? 'bg-amber-50 text-amber-800 border-amber-300 font-bold' : 'bg-gray-50 text-gray-600 border-gray-200'
          )}>
            <span className={alerts.lowStock > 0 ? 'text-amber-500' : 'text-gray-400'}>Low:</span> {alerts.lowStock}
          </span>
          <span className={clsx(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium border',
            alerts.expiry > 0 ? 'bg-rose-50 text-rose-800 border-rose-300 font-bold' : 'bg-gray-50 text-gray-600 border-gray-200'
          )}>
            <span className={alerts.expiry > 0 ? 'text-rose-400' : 'text-gray-400'}>Exp:</span> {alerts.expiry}
          </span>
        </div>
      ),
      iconBg: 'bg-orange-100 text-orange-500',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Overview Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">Here is what's happening at your pharmacy today.</p>
      </div>

      {/* Main Metric Cards Grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {metrics.map((m) => (
          <div
            key={m.label}
            className={clsx(
              "flex flex-col justify-between rounded-xl border p-4 shadow-sm transition-all hover:shadow-md",
              m.highlight
                ? "border-emerald-200 bg-gradient-to-br from-emerald-50/50 via-white to-white ring-1 ring-emerald-400/30"
                : "border-gray-200 bg-white"
            )}
          >
            <div>
              <div className="flex items-start justify-between gap-1">
                <div className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', m.iconBg)}>
                  <m.icon className="h-5 w-5" />
                </div>
                <span className={clsx(
                  "text-xl sm:text-2xl font-bold truncate text-right",
                  m.highlight ? "text-emerald-700" : "text-gray-900"
                )}>
                  {m.value}
                </span>
              </div>
              <p className="mt-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                {m.label}
              </p>
            </div>
            <div className="mt-2.5 pt-2 border-t border-gray-100/80">
              {m.badge}
            </div>
          </div>
        ))}
      </div>

      {/* Financial & Activity Breakdown Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {/* Remaining Cash Breakdown */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100">
                <Wallet className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Cash In Hand</h3>
                <p className="text-[10px] text-gray-400">Available Liquid Cash</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-bold text-emerald-700">Rs. {formatCurrency(remainingCash.totalBalance)}</span>
            </div>
          </div>
          <div className="mt-2.5 space-y-1 text-xs">
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Today Cash In</span>
              <span className="text-[11px] font-semibold text-emerald-600">Rs. {formatCurrency(remainingCash.todayCashIn)}</span>
            </div>
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Today Cash Out</span>
              <span className="text-[11px] font-semibold text-rose-600">Rs. {formatCurrency(remainingCash.todayCashOut)}</span>
            </div>
          </div>
        </div>

        {/* Today Sales Breakdown */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-600 border border-blue-100">
                <ShoppingCart className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Sales Breakdown</h3>
                <p className="text-[10px] text-gray-400">{todaySales.orders} Orders Today</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-bold text-blue-700">Rs. {formatCurrency(todaySales.total)}</span>
            </div>
          </div>
          <div className="mt-2.5 space-y-1 text-xs">
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Cash Sales</span>
              <span className="text-[11px] font-semibold text-emerald-600">Rs. {formatCurrency(todaySales.cash)}</span>
            </div>
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Card / Bank</span>
              <span className="text-[11px] font-semibold text-blue-600">Rs. {formatCurrency(todaySales.card + todaySales.online)}</span>
            </div>
            {Number(todaySales.credit) > 0 && (
              <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
                <span className="text-[11px] text-gray-600 font-medium">Credit (Udhaar)</span>
                <span className="text-[11px] font-semibold text-amber-600">Rs. {formatCurrency(todaySales.credit)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Staff Payouts Breakdown */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100">
                <UserCheck className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Staff Payouts</h3>
                <p className="text-[10px] text-gray-400">Salary & Advance</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-bold text-indigo-700">Rs. {formatCurrency(staffPaid.todayTotal)}</span>
            </div>
          </div>
          <div className="mt-2.5 space-y-1 text-xs">
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Salary Paid (Today)</span>
              <span className="text-[11px] font-semibold text-indigo-600">Rs. {formatCurrency(staffPaid.todaySalary)}</span>
            </div>
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Advance Paid (Today)</span>
              <span className="text-[11px] font-semibold text-purple-600">Rs. {formatCurrency(staffPaid.todayAdvance)}</span>
            </div>
            <div className="flex items-center justify-between rounded bg-indigo-50/50 px-2.5 py-1 border border-indigo-100/60">
              <span className="text-[10px] text-indigo-700 font-medium">Month (Sal/Adv)</span>
              <span className="text-[10px] font-bold text-indigo-800">
                Rs. {formatCurrency(staffPaid.monthSalary)} / {formatCurrency(staffPaid.monthAdvance)}
              </span>
            </div>
          </div>
        </div>

        {/* Udhaar Transactions Breakdown */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-50 text-teal-600 border border-teal-100">
                <ArrowDownLeft className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Udhaar Activity</h3>
                <p className="text-[10px] text-gray-400">Recovered vs Given</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-bold text-teal-700">Rec: Rs. {formatCurrency(udhaarReceived.monthTotal)}</span>
            </div>
          </div>
          <div className="mt-2.5 space-y-1 text-xs">
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Udhaar Received Today</span>
              <span className="text-[11px] font-semibold text-teal-700">Rs. {formatCurrency(udhaarReceived.todayTotal)}</span>
            </div>
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Udhaar Given Today</span>
              <span className="text-[11px] font-semibold text-amber-700">Rs. {formatCurrency(udhaarGiven.todayTotal)}</span>
            </div>
          </div>
        </div>

        {/* Advance Payments Summary */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-50 text-violet-600 border border-violet-100">
                <Wallet className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Advance Payments</h3>
                <p className="text-[10px] text-gray-400">Current advance balances</p>
              </div>
            </div>
          </div>
          <div className="mt-2.5 space-y-1 text-xs">
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Customer Advance Received ({advances.customerCount})</span>
              <span className="text-[11px] font-semibold text-teal-700">Rs. -{formatCurrency(advances.customerReceived)}</span>
            </div>
            <div className="flex items-center justify-between rounded bg-gray-50/75 px-2.5 py-1.5">
              <span className="text-[11px] text-gray-600 font-medium">Supplier Advance Paid ({advances.supplierCount})</span>
              <span className="text-[11px] font-semibold text-purple-700">Rs. +{formatCurrency(advances.supplierPaid)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              <h2 className="text-base font-semibold text-gray-900">Revenue Trend</h2>
            </div>
            <span className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600">
              Last 7 Days
            </span>
          </div>
          <div className="px-6 py-8">
            <div className="flex items-end gap-2 h-40">
              {trend.map((day, i) => {
                const maxVal = Math.max(...trend.map((d) => d.total), 1);
                const height = (day.total / maxVal) * 100;
                return (
                  <div key={i} className="flex h-full flex-1 flex-col items-center gap-1">
                    <div className="flex w-full flex-1 items-end">
                      <div
                        className="w-full rounded-t bg-emerald-400"
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-400">{day.date}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
            <Trophy className="h-5 w-5 text-amber-500" />
            <h2 className="text-base font-semibold text-gray-900">Top Sellers</h2>
          </div>
          {topSellers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Archive className="mb-2 h-12 w-12 text-gray-300" />
              <p className="text-sm">No sales data</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {topSellers.map((item, i) => (
                <div key={i} className="flex items-center justify-between px-6 py-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                    <p className="text-xs text-gray-500">Qty: {item.qty}</p>
                  </div>
                   <span className="text-sm font-bold text-emerald-600">Rs. {formatCurrency(item.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <h2 className="text-base font-semibold text-gray-900">Expiry Alerts</h2>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {expiryAlerts.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-400">No expiry alerts</div>
            ) : (
              expiryAlerts.map((item, i) => (
                <div key={i} className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                    <p className="text-xs text-gray-500">
                      Batch: {item.batch_number} · Qty: {item.quantity}
                    </p>
                  </div>
                  <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-600">
                    {item.days_left} days
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-500" />
              <h2 className="text-base font-semibold text-gray-900">Low Stock Warning</h2>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {lowStock.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Package className="mb-2 h-12 w-12 text-gray-300" />
                <p className="text-sm">All medicines well-stocked!</p>
              </div>
            ) : (
              lowStock.map((item, i) => (
                <div key={i} className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                    <p className="text-xs text-gray-500">
                      Reorder at: {item.reorder_level}
                    </p>
                  </div>
                  <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-600">
                    {item.stock} left
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="h-4" />
    </div>
  );
}
